import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Lock, MoreHorizontal, Plus, Pencil, Trash2, Check, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({ title: "", excerpt: "", content: "", published: true });

  const headers = { Authorization: `Bearer ${token}` };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      sessionStorage.setItem("admin_token", res.data.token);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setToken("");
    sessionStorage.removeItem("admin_token");
  };

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`, { headers });
      setBookings(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  }, [token]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/all`, { headers });
      setPosts(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchBookings();
      fetchPosts();
    }
  }, [token, fetchBookings, fetchPosts]);

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { status }, { headers });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleSavePost = async () => {
    if (!postForm.title || !postForm.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, postForm, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, postForm, { headers });
        toast.success("Post created");
      }
      setShowEditor(false);
      setEditingPost(null);
      setPostForm({ title: "", excerpt: "", content: "", published: true });
      fetchPosts();
    } catch {
      toast.error("Failed to save post");
    }
  };

  const handleDeletePost = async (id) => {
    try {
      await axios.delete(`${API}/blog/${id}`, { headers });
      toast.success("Post deleted");
      fetchPosts();
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setPostForm({ title: post.title, excerpt: post.excerpt, content: post.content, published: post.published });
    } else {
      setEditingPost(null);
      setPostForm({ title: "", excerpt: "", content: "", published: true });
    }
    setShowEditor(true);
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-[#E6DCCA] text-[#2D2D2D]",
      confirmed: "bg-[#A8BBA2] text-white",
      cancelled: "bg-[#B95D5D]/20 text-[#B95D5D]",
    };
    return (
      <span className={`text-xs font-medium px-3 py-1 rounded-full ${styles[status] || ""}`}>
        {status}
      </span>
    );
  };

  // Login Screen
  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-[#FDFBF7] pt-28 pb-24 flex items-center justify-center">
        <div className="w-full max-w-sm mx-auto px-6">
          <div className="text-center mb-10">
            <div className="w-12 h-12 rounded-full bg-[#F5F2EB] flex items-center justify-center mx-auto mb-4">
              <Lock size={20} className="text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-medium text-[#2D2D2D]">Admin Access</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border-[#E6DCCA] h-12"
              placeholder="Enter admin password"
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loggingIn}
              className="btn-gold w-full"
            >
              {loggingIn ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  // Admin Dashboard
  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7] pt-28 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-medium text-[#2D2D2D]">Dashboard</h1>
            <p className="text-sm text-[#6B6B6B] mt-1">Manage bookings and blog posts</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="text-sm text-[#6B6B6B] hover:text-[#B95D5D] transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-8 border-b border-[rgba(45,45,45,0.08)] mb-8">
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`pb-3 text-sm font-medium transition-colors ${
              tab === "bookings"
                ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                : "text-[#6B6B6B] hover:text-[#2D2D2D]"
            }`}
          >
            Bookings ({bookings.length})
          </button>
          <button
            data-testid="admin-tab-blog"
            onClick={() => { setTab("blog"); setShowEditor(false); }}
            className={`pb-3 text-sm font-medium transition-colors ${
              tab === "blog"
                ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                : "text-[#6B6B6B] hover:text-[#2D2D2D]"
            }`}
          >
            Blog Posts ({posts.length})
          </button>
        </div>

        {/* Bookings Tab */}
        {tab === "bookings" && (
          <div data-testid="admin-bookings-list">
            {bookings.length === 0 ? (
              <p className="text-[#6B6B6B] text-center py-16">No bookings yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[rgba(45,45,45,0.08)]">
                      <th className="text-left text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3 pr-4">Client</th>
                      <th className="text-left text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3 pr-4">Service</th>
                      <th className="text-left text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3 pr-4">Date</th>
                      <th className="text-left text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3 pr-4">Time</th>
                      <th className="text-left text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3 pr-4">Status</th>
                      <th className="text-right text-xs font-medium tracking-widest uppercase text-[#6B6B6B] py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-b border-[rgba(45,45,45,0.04)]" data-testid={`booking-row-${b.id}`}>
                        <td className="py-4 pr-4">
                          <p className="text-sm font-medium text-[#2D2D2D]">{b.name}</p>
                          <p className="text-xs text-[#6B6B6B]">{b.email}</p>
                        </td>
                        <td className="py-4 pr-4 text-sm text-[#2D2D2D]">{b.service_name}</td>
                        <td className="py-4 pr-4 text-sm text-[#6B6B6B]">{b.date}</td>
                        <td className="py-4 pr-4 text-sm text-[#6B6B6B]">{b.time_slot}</td>
                        <td className="py-4 pr-4">{statusBadge(b.status)}</td>
                        <td className="py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger data-testid={`booking-actions-${b.id}`} className="p-1 hover:bg-[#F5F2EB] rounded">
                              <MoreHorizontal size={16} className="text-[#6B6B6B]" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                data-testid={`confirm-booking-${b.id}`}
                                onClick={() => updateBookingStatus(b.id, "confirmed")}
                              >
                                <Check size={14} className="mr-2 text-[#A8BBA2]" /> Confirm
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`cancel-booking-${b.id}`}
                                onClick={() => updateBookingStatus(b.id, "cancelled")}
                              >
                                <X size={14} className="mr-2 text-[#B95D5D]" /> Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Blog Tab */}
        {tab === "blog" && !showEditor && (
          <div data-testid="admin-blog-list">
            <div className="flex justify-end mb-6">
              <button
                data-testid="admin-new-post-btn"
                onClick={() => openEditor()}
                className="btn-gold inline-flex items-center gap-2 text-xs"
              >
                <Plus size={14} /> New Post
              </button>
            </div>
            {posts.length === 0 ? (
              <p className="text-[#6B6B6B] text-center py-16">No blog posts yet</p>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    data-testid={`admin-post-${post.id}`}
                    className="bg-white border border-[#F5F2EB] p-6 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-base font-medium text-[#2D2D2D]">{post.title}</h3>
                        {post.published ? (
                          <Badge className="bg-[#A8BBA2] text-white text-xs border-0">Published</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Draft</Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#6B6B6B]">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`edit-post-${post.id}`}
                        onClick={() => openEditor(post)}
                        className="p-2 hover:bg-[#F5F2EB] rounded transition-colors"
                      >
                        <Pencil size={14} className="text-[#6B6B6B]" />
                      </button>
                      <button
                        data-testid={`delete-post-${post.id}`}
                        onClick={() => handleDeletePost(post.id)}
                        className="p-2 hover:bg-[#B95D5D]/10 rounded transition-colors"
                      >
                        <Trash2 size={14} className="text-[#B95D5D]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blog Editor */}
        {tab === "blog" && showEditor && (
          <div data-testid="admin-blog-editor" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-medium text-[#2D2D2D]">
                {editingPost ? "Edit Post" : "New Post"}
              </h3>
              <button
                data-testid="admin-editor-cancel"
                onClick={() => { setShowEditor(false); setEditingPost(null); }}
                className="text-sm text-[#6B6B6B] hover:text-[#2D2D2D]"
              >
                Cancel
              </button>
            </div>

            <div>
              <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Title</label>
              <Input
                data-testid="admin-post-title"
                value={postForm.title}
                onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                className="bg-white border-[#E6DCCA] h-12"
                placeholder="Post title"
              />
            </div>
            <div>
              <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Excerpt</label>
              <Input
                data-testid="admin-post-excerpt"
                value={postForm.excerpt}
                onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                className="bg-white border-[#E6DCCA] h-12"
                placeholder="Brief summary"
              />
            </div>
            <div>
              <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Content</label>
              <Textarea
                data-testid="admin-post-content"
                value={postForm.content}
                onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                className="bg-white border-[#E6DCCA] min-h-[300px]"
                placeholder="Write your post content..."
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="admin-post-toggle-published"
                onClick={() => setPostForm({ ...postForm, published: !postForm.published })}
                className="flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#2D2D2D] transition-colors"
              >
                {postForm.published ? <Eye size={14} /> : <EyeOff size={14} />}
                {postForm.published ? "Published" : "Draft"}
              </button>
            </div>
            <div className="flex gap-4 pt-4">
              <button
                data-testid="admin-post-save"
                onClick={handleSavePost}
                className="btn-gold"
              >
                {editingPost ? "Update Post" : "Publish Post"}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
